import { DEVICE_TYPE_BREAKPOINTS, type DeviceType } from '@talos/kyoko';
import type { LayoutSpec } from '../../../types/LayoutConfig';

/** Device types ordered from smallest to largest viewport, derived from DEVICE_TYPE_BREAKPOINTS. */
const DEVICE_TYPE_SIZE_ORDER: readonly DeviceType[] = (
  Object.entries(DEVICE_TYPE_BREAKPOINTS) as [keyof typeof DEVICE_TYPE_BREAKPOINTS, number | undefined][]
)
  .toSorted(([, a], [, b]) => (a ?? Number.POSITIVE_INFINITY) - (b ?? Number.POSITIVE_INFINITY))
  .map(([key]) => key);

/**
 * Returns device types to try in order when resolving a layout: current device type first,
 * then larger sizes only (e.g. tablet → laptop → desktop → widescreen). Caller handles 'all'
 * as a final fallback.
 */
export function getDeviceTypeFallbackOrder(deviceType: DeviceType): DeviceType[] {
  if (deviceType === 'all') {
    return [...DEVICE_TYPE_SIZE_ORDER];
  }
  const index = DEVICE_TYPE_SIZE_ORDER.indexOf(deviceType);
  if (index === -1) {
    return [...DEVICE_TYPE_SIZE_ORDER];
  }
  return [...DEVICE_TYPE_SIZE_ORDER.slice(index)];
}

function pickPreferred(
  matches: [string, LayoutSpec][],
  targetType: DeviceType,
  preferredLayoutId: string | undefined,
  layouts: Record<string, LayoutSpec>
): string {
  const preferred =
    preferredLayoutId != null &&
    layouts[preferredLayoutId] != null &&
    (layouts[preferredLayoutId].deviceType ?? 'all') === targetType
      ? preferredLayoutId
      : null;
  return preferred ?? matches[0][0];
}

/**
 * Returns a layout id from `layouts` that matches the current device type or any larger size
 * in the fallback order, then 'all'. Prefers exact device-type match over 'all'. When multiple
 * layouts match at a level, prefers `preferredLayoutId` if it is among them. Treats missing
 * layout.deviceType as 'all'.
 */
export function getLayoutIdForDevice(
  layouts: Record<string, LayoutSpec>,
  deviceType: DeviceType,
  preferredLayoutId?: string
): string | undefined {
  const order = getDeviceTypeFallbackOrder(deviceType);
  const layoutEntries = Object.entries(layouts);

  for (const targetType of order) {
    const matches = layoutEntries.filter(([, spec]) => (spec.deviceType ?? 'all') === targetType);
    if (matches.length > 0) {
      return pickPreferred(matches, targetType, preferredLayoutId, layouts);
    }
  }
  const allMatches = layoutEntries.filter(([, spec]) => (spec.deviceType ?? 'all') === 'all');
  if (allMatches.length > 0) {
    return pickPreferred(allMatches, 'all', preferredLayoutId, layouts);
  }
  return undefined;
}
